<template>
  <div class="q-px-lg">
    <div class="row q-col-gutter-xl">
      <div class="col-12 q-mt-xl">
        <div class="flex items-center justify-between">
          <PBtn :label="`View History`" flat dense @click="openTimelines" />
          <PBtn flat dense @click="openAddCommunicationDialog">
            <AddIcon class="icon--sm q-mr-xxs" /> Add
          </PBtn>
        </div>
        <q-separator spaced />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center" style="min-height: 200px">
      <q-linear-progress indeterminate color="primary" />
    </div>
    <div v-else>
      <div
        v-for="communication in formattedCommunications"
        :key="communication._id"
        class="q-my-lg"
      >
        <div class="text-md text-weight-medium q-mb-sm">
          {{ communication.name }}
        </div>

        <PTable
          v-if="communication.recipients?.length"
          :columns="columns"
          :rows="communication.recipients"
          :row-key="'_id'"
          hide-bottom
          flat
          class="text-xs"
          no-data-message="No recipients found"
          table-header-class="text-neutral text-weight-semibold"
        >
          <template #body="rowProps">
            <q-tr :props="rowProps">
              <q-td key="name" :props="rowProps" :style="{ width: '150px' }">
                <PPopover :value="rowProps.row.name || '-'" :max-length="15" />
              </q-td>
              <q-td key="phone" :props="rowProps" :style="{ width: '150px' }">
                {{ rowProps.row.phone || "-" }}
              </q-td>
              <q-td key="email" :props="rowProps" :style="{ width: '150px' }">
                <PPopover :value="rowProps.row.email || '-'" :max-length="15" />
              </q-td>
              <q-td key="status_cd" :props="rowProps">
                <PBadge
                  :type="normalizeStatus(rowProps.row.status_cd).value"
                  :label="normalizeStatus(rowProps.row.status_cd).label"
                />
              </q-td>
              <q-td key="action" :props="rowProps">
                <PBtn flat dense>
                  <MenuIcon class="icon" />
                  <q-menu
                    :offset="[0, 8]"
                    anchor="bottom right"
                    self="top right"
                    fit
                    :style="{ width: '180px', borderRadius: '8px' }"
                  >
                    <q-list>
                      <q-item
                        v-for="(
                          { label, icon, disable, classes, onClick }, index
                        ) in getMenuItems(rowProps.row)"
                        :key="index"
                        v-close-popup
                        clickable
                        dense
                        :disable="disable"
                        @click="onClick"
                      >
                        <q-item-section>
                          <q-item-label
                            class="flex items-center text-sm text-primary-100 text-weight-semibold"
                          >
                            <component
                              :is="icon"
                              :class="`icon--xs q-mr-xs ${classes}`"
                            />
                            <span :class="classes">{{ label }}</span>
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </PBtn>
              </q-td>
            </q-tr>
          </template>
        </PTable>
        <div v-else class="text-neutral-70 text-sm">No contact added</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useQuasar } from "quasar";

import MenuIcon from "assets/icons/menu.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import DisableIcon from "assets/icons/deactivate.svg";
import AddIcon from "assets/icons/add.svg";

import { putStatus } from "src/api/communication-settings";

import PDialogForm from "src/components/PDialogForm.vue";
import AddCommunicationDialog from "src/modules/user/components/AddCommunicationSettingDialogForm.vue";

import { useChangeCase } from "@vueuse/integrations/useChangeCase";

// Constants
const STATUS = {
  ACTIVE: "active",
  DISABLED: "disabled",
  REMOVED: "removed",
};

const USER_TYPE = "user";

const STATUS_MAP = {
  [STATUS.ACTIVE]: { value: "enabled", label: "Enabled" },
  [STATUS.DISABLED]: { value: "disabled", label: "Disabled" },
  [STATUS.REMOVED]: { value: "removed", label: "Removed" },
};

const columns = [
  {
    name: "name",
    label: "Name",
    field: "name",
    align: "left",
    format: (value) => value || "-",
  },
  {
    name: "phone",
    label: "Contact No.",
    field: "phone",
    align: "left",
    format: (value) => value || "-",
  },
  {
    name: "email",
    label: "Email",
    field: "email",
    align: "left",
    format: (value) => value || "-",
  },
  {
    name: "status_cd",
    label: "State",
    field: "status_cd",
    align: "center",
    format: (value) => value || "-",
  },
  {
    name: "action",
    label: "",
    field: "action",
    align: "right",
  },
];

const $q = useQuasar();

const { toggleTimelinesDrawer } = useAuditLogsStore();
const { getCommunicationSettings } = useCommunicationSettingsStore();
const { communications } = storeToRefs(useCommunicationSettingsStore());

const props = defineProps({
  userId: {
    type: String,
    required: true,
  },
  role: {
    type: String,
    required: true,
  },
});

const openAddCommunicationDialog = (recipient = null) => {
  return $q.dialog({
    component: AddCommunicationDialog,
    componentProps: {
      userId: props.userId,
      role: props.role,
      recipient,
    },
  });
};

const isLoading = ref(false);
const isMenuLoading = ref(false);
const isTimelineLoading = ref(false);

// Load communication settings
const loadCommunicationSettings = async () => {
  try {
    isLoading.value = true;

    await getCommunicationSettings({
      _id: props.userId,
      params: { role: props.role },
    });
  } catch (error) {
    console.error("Failed to load communication settings:", error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for prop changes and reload data
watch([() => props.userId, () => props.role], loadCommunicationSettings, {
  immediate: true,
});

// Computed for formatted communications
const formattedCommunications = computed(() =>
  communications.value.map((comm) => ({
    ...comm,
    recipients: comm.recipients || [],
  })),
);

// Simplified status normalization using STATUS_MAP
const normalizeStatus = (value) => {
  const lower = value?.toLowerCase();

  if (!lower) return { value: "-", label: "-" };

  return (
    STATUS_MAP[lower] || {
      value: lower,
      label: useChangeCase(lower, "capitalCase").value,
    }
  );
};

// Execute status update for recipient
const executeStatusUpdate = async (
  recipient,
  newStatus,
  confirmationDialog,
) => {
  try {
    isMenuLoading.value = true;

    await putStatus({
      user_id: props.userId,
      status_cd: newStatus,
      recipient_id: recipient._id,
      type_id: recipient.typeData._id,
    });

    confirmationDialog?.hide();

    await getCommunicationSettings({
      _id: props.userId,
      params: { role: props.role },
    });
  } catch (error) {
    console.error("Failed to update recipient status:", error);
  } finally {
    isMenuLoading.value = false;
  }
};

// Shared function to update recipient status with confirmation
const updateRecipientStatus = async (recipient, newStatus, actionLabel) => {
  const { typeData } = recipient;

  const confirmationDialog = $q.dialog({
    component: PDialogForm,
    componentProps: {
      title: actionLabel,
      text: `Are you sure you want to ${actionLabel.toLowerCase()} ${typeData.name} for ${recipient.name}?`,
      cardStyle: { minWidth: "600px" },
      submitLabel: "Yes",
      cancelLabel: "Cancel",
      cancellable: true,
      closable: true,
      form: {},
      loading: isMenuLoading,
      onConfirm: () =>
        executeStatusUpdate(recipient, newStatus, confirmationDialog),
    },
  });
};

// Handle toggle status (enable/disable)
const handleToggleStatus = (recipient) => {
  const isActive = recipient.status_cd === STATUS.ACTIVE;
  const action = isActive ? "Disable" : "Enable";
  const newStatus = isActive ? STATUS.DISABLED : STATUS.ACTIVE;

  updateRecipientStatus(recipient, newStatus, action);
};

// Handle remove recipient
const handleRemoveRecipient = (recipient) => {
  updateRecipientStatus(recipient, STATUS.REMOVED, "Remove");
};

// Get menu items for each recipient
const getMenuItems = (recipient) => {
  const isActive = recipient.status_cd === STATUS.ACTIVE;
  const isUserType = recipient.type === USER_TYPE;

  return [
    {
      label: isActive ? "Disable" : "Enable",
      icon: DisableIcon,
      onClick: () => handleToggleStatus(recipient),
    },
    {
      label: "Edit",
      icon: EditIcon,
      onClick: () => openAddCommunicationDialog(recipient),
    },
    {
      label: "Remove",
      icon: RemoveIcon,
      disable: isUserType,
      onClick: () => handleRemoveRecipient(recipient),
    },
  ].map((item) => ({ ...item, classes: "text-primary-100" }));
};

const openTimelines = async () => {
  try {
    isTimelineLoading.value = true;

    toggleTimelinesDrawer({
      open: true,
      level: 0,
      props: {
        entityId: props.userId,
        entityName: "Communications",
        heading: "Activity Logs",
      },
    });
  } catch (err) {
    console.error("Failed to open timelines:", err);
  } finally {
    isTimelineLoading.value = false;
  }
};
</script>

<style scoped>
:deep(.q-table) {
  table-layout: fixed;
}
</style>
